import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, Radio, ExternalLink, X as XIcon } from "lucide-react";

// Cast Calendar (Iter27)
// A caster-only view showing every fixture they've signed up to stream, grouped by day.
// Highlights kickoff conflicts (fixtures within 90 minutes of each other) so double-bookings never
// slip past a busy shoutcaster.
export default function CastCalendar() {
  const { user } = useAuth();
  const [data, setData] = useState({ streamer: null, fixtures: [], conflicts: [] });
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/streamers/me/cast-calendar").then((r) => setData(r.data)),
      api.get("/teams").then((r) => setTeams(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamById = (id) => teams.find((t) => t.id === id);
  const conflictIds = useMemo(() => {
    const s = new Set();
    for (const c of data.conflicts || []) { s.add(c.a); s.add(c.b); }
    return s;
  }, [data.conflicts]);

  const groups = useMemo(() => {
    const g = {};
    for (const f of data.fixtures || []) {
      const key = f.scheduled_at ? new Date(f.scheduled_at).toDateString() : "TBD";
      (g[key] ||= []).push(f);
    }
    return Object.entries(g).sort(([a], [b]) => (a === "TBD" ? 1 : new Date(a) - new Date(b)));
  }, [data.fixtures]);

  const drop = async (fid) => {
    if (!window.confirm("Drop this cast signup?")) return;
    try {
      await api.delete(`/fixtures/${fid}/streamer-signup`);
      toast.success("Cast signup dropped.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user === null) return <div className="text-gray-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div className="space-y-8" data-testid="cast-calendar-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#B084FF] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" /> / CAST CALENDAR
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">
            Your Cast Slate
          </h1>
          <p className="text-gray-400 mt-2 max-w-xl text-sm">
            Every fixture you've signed up to stream, sorted by kickoff. Conflicts flagged in magenta —
            drop one before you double-book yourself.
          </p>
        </div>
        <Link
          to="/schedule"
          data-testid="cast-calendar-add"
          className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold text-xs px-4 py-3 flex items-center gap-2"
        >
          <CalendarClock className="w-4 h-4" /> Sign up for more
        </Link>
      </div>

      {!data.streamer && !loading && (
        <div className="border border-[#F4D03F] bg-[#F4D03F]/5 p-6" data-testid="cast-calendar-not-caster">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-1">Not a caster yet</div>
          <div className="font-heading font-black text-xl uppercase tracking-tight">Register a streamer dossier first</div>
          <p className="text-sm text-gray-300 mt-2">
            Fill out the streamer application to start signing up for casts.
          </p>
          <Link
            to="/streamers/signup"
            className="mt-3 inline-block text-[10px] font-mono uppercase tracking-widest border border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-3 py-1.5"
          >
            Apply →
          </Link>
        </div>
      )}

      {data.streamer && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Cast slots booked" value={data.fixtures.length} tint="text-[#B084FF]" />
          <StatCard label="Kickoff conflicts" value={data.conflicts.length} tint={data.conflicts.length ? "text-[#FF007F]" : "text-gray-500"} />
          <StatCard label="Verified status" value={data.streamer.verified ? "✓" : "PENDING"} tint={data.streamer.verified ? "text-cyan-400" : "text-[#F4D03F]"} />
          <StatCard label="Handle" value={data.streamer.handle || "—"} tint="text-white" />
        </div>
      )}

      {loading && data.fixtures.length === 0 && (
        <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">Loading…</div>
      )}

      {data.streamer && !loading && data.fixtures.length === 0 && (
        <div className="border border-[#232330] p-8 text-center" data-testid="cast-calendar-empty">
          <CalendarClock className="w-10 h-10 mx-auto text-gray-600 mb-3" />
          <div className="font-heading font-black uppercase tracking-tight text-xl mb-1">No casts booked</div>
          <div className="text-xs font-mono uppercase tracking-widest text-gray-500">Head to the schedule and grab a match.</div>
        </div>
      )}

      {groups.map(([day, fixes]) => (
        <section key={day} data-testid={`cast-calendar-day-${day.replace(/\s+/g, "_")}`} className="space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 border-b border-[#232330] pb-2">
            {day}
          </div>
          {fixes.map((f) => {
            const h = teamById(f.home_team_id) || { tag: "TBD" };
            const a = teamById(f.away_team_id) || { tag: "TBD" };
            const isConflict = conflictIds.has(f.id);
            return (
              <div
                key={f.id}
                data-testid={`cast-calendar-row-${f.id}`}
                className={`border p-4 flex items-center justify-between gap-4 flex-wrap ${
                  isConflict ? "border-[#FF007F] bg-[#FF007F]/10" : "border-[#232330] bg-[#101015] hover:border-[#B084FF]/60"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-black text-lg uppercase tracking-tight">
                      <span style={{ color: h.logo_color }}>{h.tag}</span>
                      <span className="text-gray-500 mx-2">vs</span>
                      <span style={{ color: a.logo_color }}>{a.tag}</span>
                    </span>
                    {isConflict && (
                      <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-[#FF007F] text-[#FF007F] bg-[#FF007F]/20 px-2 py-0.5" data-testid={`cast-conflict-${f.id}`}>
                        <AlertTriangle className="w-3 h-3" /> Conflict
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                    {f.scheduled_at ? new Date(f.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }) : "TBD"}
                    {f.division ? ` · ${f.division}` : ""}
                    {f.map ? ` · ${f.map}` : ""}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/live/${f.id}`}
                    data-testid={`cast-calendar-open-${f.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Live page
                  </Link>
                  <button
                    onClick={() => drop(f.id)}
                    data-testid={`cast-calendar-drop-${f.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F] border border-[#232330] hover:border-[#FF007F] px-3 py-1.5 flex items-center gap-1"
                  >
                    <XIcon className="w-3 h-3" /> Drop
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function StatCard({ label, value, tint }) {
  return (
    <div className="border border-[#232330] bg-[#101015] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading font-black text-3xl mt-1 ${tint}`}>{value}</div>
    </div>
  );
}
